/**
 * Approved units, pulled onto the item.
 *
 * A unit the customer asks in that the UOM master lacks is REQUESTED from the RFQ line
 * (`RequestUomLink` → `items.uom_creation_request`) and approved elsewhere. Approval alone
 * changes nothing here: the item's conversions come from the legacy ERP, and it is the
 * connector's `pull_item_uom {item_codes}` that copies them across (Akhil, 2026-09-22).
 *
 * Measured live: `UOM Creation Request` is submittable — approved is `docstatus: 1` — and each
 * request names the enquiry (`reference_name`), the item and the line. `pull_item_uom` answers
 * `{}` (~1s) and is the one method the app calls outside the `quoflow.*` namespace.
 *
 * So the RFQ page reads the enquiry's approved requests, pulls the items whose approved unit
 * is not on them yet, refetches those items, and says which unit is now available.
 */
import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { postDotted } from './bosun'
import { getResource } from './frappe'
import { str } from './wire'
import { qk } from './keys'

export const PULL_ITEM_UOM_METHOD = 'quoflow_connector.quoflow_connector.sync.sync_manager.pull_item_uom'

export interface ApprovedUomRequest {
  uom: string
  item: string
}

interface ItemUnits {
  code: string
  uom_conversions: readonly { uom: string }[]
}

/** The items to pull: those with an approved unit not yet among their conversions. */
export function uomsToPull(approved: readonly ApprovedUomRequest[], items: ReadonlyMap<string, ItemUnits>): { codes: string[]; units: ApprovedUomRequest[] } {
  const units: ApprovedUomRequest[] = []
  for (const request of approved) {
    const item = items.get(request.item)
    if (!item || request.uom.trim() === '') continue
    const wanted = request.uom.trim().toLowerCase()
    if (item.uom_conversions.some((c) => c.uom.trim().toLowerCase() === wanted)) continue
    units.push({ item: request.item, uom: request.uom.trim() })
  }
  return { codes: [...new Set(units.map((u) => u.item))], units }
}

export async function pullItemUoms(codes: readonly string[]): Promise<void> {
  if (codes.length === 0) return
  await postDotted<unknown>(PULL_ITEM_UOM_METHOD, { item_codes: [...codes] })
}

/** The enquiry's approved requests — polled, since approval happens in another screen. */
export function useApprovedUomRequests(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: ['uom-requests', 'approved', enquiryId ?? ''] as const,
    enabled: !!enquiryId,
    refetchInterval: 60_000,
    queryFn: async (): Promise<ApprovedUomRequest[]> => {
      const page = await getResource<{ uom?: unknown; item?: unknown }>('UOM Creation Request', {
        fields: ['name', 'uom', 'item'],
        filters: [
          ['reference_name', '=', enquiryId ?? ''],
          ['docstatus', '=', 1],
        ],
        limit: 0,
      })
      return page.data.map((r) => ({ uom: str(r.uom), item: str(r.item) })).filter((r) => r.uom !== '' && r.item !== '')
    },
  })
}

/**
 * Pull approved units onto this page's items as they appear. Each item is pulled once per
 * page visit; `onPulled` says which units arrived, for the toast.
 */
export function useSyncApprovedUoms(
  enquiryId: string | null | undefined,
  items: ReadonlyMap<string, ItemUnits> | null,
  onPulled: (units: ApprovedUomRequest[]) => void,
) {
  const approved = useApprovedUomRequests(enquiryId)
  const queryClient = useQueryClient()
  const pulled = useRef(new Set<string>())
  const onPulledRef = useRef(onPulled)
  onPulledRef.current = onPulled
  useEffect(() => {
    if (!items || !approved.data || approved.data.length === 0) return
    const plan = uomsToPull(approved.data, items)
    const codes = plan.codes.filter((c) => !pulled.current.has(c))
    if (codes.length === 0) return
    for (const c of codes) pulled.current.add(c)
    void pullItemUoms(codes)
      .then(async () => {
        await Promise.all(codes.map((c) => queryClient.invalidateQueries({ queryKey: qk.item(c) })))
        await queryClient.invalidateQueries({ queryKey: ['items', 'by-codes'] })
        onPulledRef.current(plan.units.filter((u) => codes.includes(u.item)))
      })
      .catch(() => {
        // Retry on the next poll — a failed pull must not be remembered as done.
        for (const c of codes) pulled.current.delete(c)
      })
  }, [approved.data, items, queryClient])
  return { refetch: approved.refetch, checking: approved.isFetching }
}
